import { Menu, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterComponent, AdvFilterResult } from "components/AdvFilter";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonFiltrerBlocked from "components/Buttons/IconButtonFiltrerBlocked";
import IconButtonOrderBy from "components/Buttons/IconButtonOrderby";
import { RequestIndicator } from "components/CallServer";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import LibUtils from "lib/LibUtils";
import { BadgeAdminListDto, BadgeListFilter, BadgeListOrderBy, GetBadgeListParams, PagedResult } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import BadgeCard from "./Components/BadgeCard";
import BadgeEdit from "./Components/BadgeEdit";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    orderByMenuAnchorEl: any;
    dialog: any;
    editBadgeId: string;
    list: PagedResult<BadgeAdminListDto>;
    advFilters: AdvFilterResult[];
    offset: number;
    formCycle: RequestLifeCycle;
    lastError: any;
}

interface IProps {
}

type Props = IProps & WithStyles<typeof TimelineStyleClasses> & WithStyles<typeof FeedStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class BadgeAdminList extends React.Component<Props, IState> {

    private filter: BadgeListFilter;
    private orderBy: BadgeListOrderBy = BadgeListOrderBy.Name;
    private advFilterRef: AdvFilterComponent = null;

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            list: { items: null, total: 0 },
            advFilters: [],
            offset: 0,
            orderByMenuAnchorEl: null,
            dialog: null,
            editBadgeId: null,
            lastError: null,
            formCycle: RequestLifeCycle.None
        };

        this.resetFilter();
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Gamification.BadgeAdminList." + r, f);
    }

    ///
    private init() {
        this.loadData(0);
    }

    ///
    private loadData(offset: number) {
        const p: GetBadgeListParams = {
            filter: this.filter,
            orderBy: this.orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ editBadgeId: null, formCycle: RequestLifeCycle.Loading, orderByMenuAnchorEl: null, dialog: null });

        api.getAdminBadgeList(p)
            .then((result) => {
                this.setState({ list: result, formCycle: null, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, formCycle: RequestLifeCycle.LoadFailed, lastError: error, offset: 0 });
            });
    }

    ///
    private handleShowInactive = () => {
        this.filter.isActive = !this.filter.isActive;
        this.loadData(0);
    };

    ///
    private renderAdvFilter(): JSX.Element {
        const canAdd = true;

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        {
                            canAdd &&
                            <div>
                                <IconButtonAdd
                                    color="inherit"
                                    title={this.res("ButtonNewText")}
                                    onClick={() => this.handleEdit("create")}
                                />
                                <MuiClickable onClick={() => this.handleEdit("create")}>
                                    <Typography variant="button">
                                        {this.res("ButtonNewText")}
                                    </Typography>
                                </MuiClickable>
                            </div>
                        }

                        <MuiPushRight>
                            <IconButtonFiltrerBlocked
                                color={this.filter.isActive ? "inherit" : "warning"}
                                title={this.res("ShowInactiveBadges")}
                                onClick={this.handleShowInactive}
                            />

                            <IconButtonOrderBy
                                color="inherit"
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }}
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Name")} value={BadgeListOrderBy.Name} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MoreWinners")} value={BadgeListOrderBy.MostWon} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MorePoints")} value={BadgeListOrderBy.MostPoints} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </MuiPushRight>
                    </MuiFilterLine>

                    <MuiFilterAdvancedContainer disableMargins>
                        <AdvFilter
                            id="advfilter"
                            commands={null}
                            innerRef={(ref) => this.advFilterRef = ref}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputSearchPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            selectedCommands={this.state.advFilters}
                            onUpdateSelection={this.updateFilter} />
                    </MuiFilterAdvancedContainer>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    private resetFilter() {
        this.filter = {
            searchText: null,
            startDate: null,
            endDate: null,
            isActive: true,
        };
    }

    ///
    private isFiltered() {
        return !LibUtils.isAllPropertiesWithValue(this.filter, null, ["isActive"]) || this.filter.isActive !== true;
    }

    ///
    private applyOrder = (orderBy: BadgeListOrderBy) => {
        this.orderBy = orderBy;
        this.loadData(0);
    };

    ///
    private updateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        if (searchText.length == 0 && advFilters.length == 0) {
            this.resetFilter();
        }
        else {
            this.filter.searchText = searchText.length == 0 ? null : searchText;
        }

        this.setState({ advFilters });

        this.loadData(0);
    };

    ///
    private handleEdit(userId: string) {
        this.setState({ editBadgeId: userId });
    }

    ///
    private handleDelete(item: BadgeAdminListDto) {
        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: item.name })}
                    onClose={(result) => {
                        if (result) {

                            this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                            api.deleteBadge(item.badgeId)
                                .then((result) => {
                                    if (result) {
                                        this.props.snackbar.showMessage(this.res("DeleteSuccess"), "success");
                                        this.loadData(this.state.offset);
                                    }
                                    else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                });
                            ;
                        }
                        this.setState({ dialog: null });
                    }}
                />
            )
        });
    }

    ///
    private renderItems() {
        const { list } = this.state;

        if (list.total == 0 && !this.isFiltered())
            return <MuiMessageBox variant="info" message={this.res("RsResultText_zero")} />;

        if (list.total == 0 && this.isFiltered())
            return (
                <MuiMessageBox
                    variant="warning"
                    action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                    message={this.res("RsFilteredResultText_zero")}
                />
            );

        return (
            <MuiContainer margin="normal">
                <MuiContainer margin="normal">
                    <CountFilter
                        count={list.total}
                        filtered={this.isFiltered()}
                        resource="Views.Gamification.BadgeAdminList.RsResultText"
                        filteredResource="Views.Gamification.BadgeAdminList.RsFilteredResultText"
                        onResetFilter={() => this.advFilterRef.resetFilter()}
                    />
                </MuiContainer>
                {
                    list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index} >
                            <BadgeCard
                                classes={this.props.classes}
                                badge={item}
                                key={index}
                                onDelete={() => this.handleDelete(item)}
                                onEdit={() => this.handleEdit(item.badgeId)}
                            />
                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { lastError, formCycle } = this.state;

        if (formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {this.state.dialog}

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                <RequestIndicator
                    open={formCycle == RequestLifeCycle.Posting}
                    type="post"
                />

                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>
                
                {
                    formCycle == RequestLifeCycle.Loading
                        ? <LoadingIndicator />
                        : this.renderItems()
                }
            </>
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public render() {
        const { editBadgeId } = this.state;

        if (editBadgeId != null)
            return (
                <BadgeEdit
                    badgeId={editBadgeId}
                    onClose={
                        (reload) => {
                            if (reload === true || this.state.list.items == null)
                                this.loadData(this.state.offset);
                            else
                                this.setState({ editBadgeId: null });
                        }
                    }
                />
            );

        return (
            <div className={this.props.classes.timelineContainer}>
                {this.renderList()}
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

export default withSnackbar(withTranslation()(withAllStyles(BadgeAdminList)));